import React, { useState, useEffect } from "react";
import {
    Box,
    TextField,
    Button,
    Typography,
    CircularProgress,
} from "@mui/material";
import { CloudUpload as UploadIcon } from "@mui/icons-material";
import FileUploadComponent from "components/FileUploadComponent";
import SwalAlertComp from "components/SwalAlert";
import { Language, SourceModalMode } from "../../../../SourceModal.types";
import BaseOverview from "./BaseOverview";

interface FileUploadOverviewProps {
    sourceId?: number;
    sourceType: string;
    language: Language;
    onUploadSuccess?: () => void;
    onClose?: () => void;
    onRefresh?: () => void;
    newFileCheck?: boolean;
    successMessage?: string;
    mode?: SourceModalMode;
}

const FileUploadOverview: React.FC<FileUploadOverviewProps> = ({
    sourceId,
    sourceType,
    language,
    onUploadSuccess,
    onClose,
    onRefresh,
    newFileCheck = true,
    successMessage = "File uploaded successfully",
    mode,
}) => {
    const [file, setFile] = useState<File | null>(null);
    const [fileUploadDomain, setFileUploadDomain] = useState("");
    const [fileError, setFileError] = useState("");
    const [buttonClicked, setButtonClicked] = useState(false);

    useEffect(() => {
        if (file?.name && !fileUploadDomain) {
            setFileUploadDomain(file.name);
        }
    }, [file]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setFileUploadDomain(value);
        if (fileError && value !== "") {
            setFileError("");
        }
    };

    const [isLoading, setIsLoading] = useState(false);
    const handleFileUpload = () => {
        if (!fileUploadDomain) {
            setFileError(
                language?.tracker_wizard?.cannot_be_blank ?? "Cannot be blank!",
            );
            return;
        }
        if (!file) {
            setFileError(
                language?.tracker_wizard?.cannot_be_blank ??
                    "Please select a file!",
            );
            return;
        }
        setIsLoading(true);
        setButtonClicked(true);
    };

    const handleUploadComplete = (status: boolean) => {
        setIsLoading(true);
        if (!status) {
            setButtonClicked(false);
            setFileUploadDomain("");
            setFile(null);

            SwalAlertComp({
                title: language?.delete_swal_modal?.success ?? "Success",
                message: successMessage,
                icon: "success",
            });

            if (onUploadSuccess) {
                onUploadSuccess();
            }

            if (onRefresh) {
                onRefresh();
            }

            if (onClose) {
                onClose();
            }
        }
        setIsLoading(false);
    };

    if (mode === "standalone") return <BaseOverview language={language} />;
    return (
        <Box
            sx={{
                border: "1px solid",
                borderColor: "grey.300",
                borderRadius: 2,
                p: 3,
                backgroundColor: "grey.50",
            }}
        >
            <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    {"Name"}
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    placeholder={"Please do not leave blank!"}
                    value={fileUploadDomain}
                    onChange={handleInputChange}
                    error={!!fileError}
                    helperText={fileError}
                    sx={{ backgroundColor: "white" }}
                />
            </Box>

            <FileUploadComponent
                file={file}
                setFile={setFile}
                setFileUploadDomain={setFileUploadDomain}
                domain={fileUploadDomain}
                source_type={sourceType}
                sourceFileID={sourceId}
                buttonClicked={buttonClicked}
                setButtonClicked={setButtonClicked}
                newFileCheck={newFileCheck}
                setFileData={handleUploadComplete}
            />

            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                <Button
                    disabled={isLoading}
                    variant="contained"
                    color="primary"
                    startIcon={
                        isLoading ? (
                            <CircularProgress size={20} />
                        ) : (
                            <UploadIcon />
                        )
                    }
                    onClick={handleFileUpload}
                >
                    {isLoading ? "Uploading..." : "Upload"}
                </Button>
            </Box>
        </Box>
    );
};

export default FileUploadOverview;
